// frontend/src/components/UI/PasswordChangeModal.js
import React, { useState, useEffect } from 'react';
import { CheckCircle, Loader2, Shield, Key, X } from 'lucide-react';
import Modal from './Modal';
import PasswordField, { PasswordRequirements } from './PasswordField';
import {
  DEFAULT_STAFF_PASSWORD,
  PASSWORD_IDENTITY_HELPER,
  passwordMeetsComplexity,
} from '../../utils/passwordPolicy';

/**
 * Password Change Modal
 *
 * Supports two modes:
 * - forced: Displays after PDPA consent when user is still using default password.
 *           Cannot be dismissed - user must change password to proceed.
 * - voluntary: User-initiated password change from dropdown menu.
 *              Can be dismissed with close button.
 *
 * Props:
 * - isOpen: boolean - Whether modal is visible
 * - onChangePassword: function(currentPassword, newPassword) - Callback when user submits
 * - onClose: function - Callback when modal is closed (only used in voluntary mode)
 * - loading: boolean - Whether change request is in progress
 * - error: string - Error message to display
 * - mode: 'forced' | 'voluntary' - Modal behavior mode (default: 'forced')
 * - step: number - Current step (1 = PDPA done, 2 = password change) - only for forced mode
 * - totalSteps: number - Total steps in onboarding flow - only for forced mode
 */
const PasswordChangeModal = ({
  isOpen,
  onChangePassword,
  onClose,
  loading = false,
  error = null,
  mode = 'forced',
  step = 2,
  totalSteps = 2
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }
  }, [isOpen]);

  const isVoluntary = mode === 'voluntary';
  const passwordsMatch = Boolean(newPassword && confirmPassword && newPassword === confirmPassword);
  const isDefaultPassword = newPassword === DEFAULT_STAFF_PASSWORD;
  const complexityMet = passwordMeetsComplexity(newPassword);
  const canSubmit = Boolean(
    currentPassword &&
    complexityMet &&
    !isDefaultPassword &&
    passwordsMatch &&
    !loading
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (canSubmit) {
      onChangePassword(currentPassword, newPassword);
    }
  };

  const handleClose = () => {
    if (isVoluntary && onClose && !loading) {
      onClose();
    }
  };

  const header = (
    <div className="bg-gradient-to-r from-blue-900 to-blue-800 px-6 py-5 sm:px-8">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm flex-shrink-0">
          {isVoluntary ? (
            <Key className="w-6 h-6 text-white" aria-hidden="true" />
          ) : (
            <Shield className="w-6 h-6 text-white" aria-hidden="true" />
          )}
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-white">
            {isVoluntary ? 'Change Password' : 'Secure Your Account'}
          </h2>
          {!isVoluntary && totalSteps > 1 && (
            <p className="text-blue-100 text-sm mt-0.5">
              Step {step} of {totalSteps}
            </p>
          )}
          {isVoluntary && (
            <p className="text-blue-100 text-sm mt-0.5">
              Update your account password
            </p>
          )}
        </div>
        {isVoluntary && (
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="icon-button-dark disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );

  const modalFooter = (
    <div className="space-y-3">
      <button
        type="submit"
        form="password-change-form"
        disabled={!canSubmit}
        className="btn btn-primary w-full"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            Updating password…
          </>
        ) : (
          isVoluntary ? 'Update Password' : 'Set New Password'
        )}
      </button>
      <p className="text-center text-xs text-gray-500">
        {isVoluntary
          ? 'A confirmation email will be sent after your password is changed.'
          : 'You must set a new password to continue using the HR Portal.'
        }
      </p>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isVoluntary ? 'Change Password' : 'Secure Your Account'}
      size="md"
      closable={isVoluntary}
      customHeader={header}
      footer={modalFooter}
    >
      <form id="password-change-form" className="space-y-4" onSubmit={handleSubmit}>
        <p className="text-sm text-gray-600">
          {isVoluntary
            ? 'Enter your current password and choose a new one. You will be logged out of other devices.'
            : 'For your security, please set a new password to replace the temporary one.'
          }
        </p>

        {error && (
          <div className="rounded-lg border border-error-200 bg-error-50 px-3 py-2.5" role="alert">
            <p className="text-sm text-error-600">{error}</p>
          </div>
        )}

        <div>
          <PasswordField
            id="current-password"
            label={isVoluntary ? 'Current Password' : 'Temporary Password'}
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder={isVoluntary ? 'Enter current password' : 'Enter temporary login password'}
            autoComplete="current-password"
          />
          {!isVoluntary && (
            <p className="mt-1.5 text-xs text-gray-500">
              Enter the password you used to log in — the last 4 characters of your NRIC/FIN followed by your date of birth (e.g. <span className="font-mono">567A01011990</span>).
            </p>
          )}
        </div>

        <div>
          <PasswordField
            id="new-password"
            label="New Password"
            helper={PASSWORD_IDENTITY_HELPER}
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password"
            autoComplete="new-password"
            error={isDefaultPassword ? 'Please choose a different password.' : undefined}
          />
          {newPassword && !isDefaultPassword && (
            <PasswordRequirements password={newPassword} />
          )}
        </div>

        <div>
          <PasswordField
            id="confirm-password"
            label="Confirm New Password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            autoComplete="new-password"
            error={confirmPassword && !passwordsMatch ? 'Passwords do not match' : undefined}
          />
          {passwordsMatch && (
            <div className="mt-1.5 flex items-center gap-1">
              <CheckCircle className="w-4 h-4 text-green-600" aria-hidden="true" />
              <span className="text-xs text-green-700">Passwords match</span>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default PasswordChangeModal;
